import { MenuItem } from 'primeng/api';
import { Roles } from '../_models';

export interface ErrorNotification {
  severity: string;
  summary: string;
  detail: string;
  life: number;
  key: string;
}

export enum ErrorCode {
  UserRoleNotFound = '0001',
  ForbiddenOperation = '0002',
  DuplicateTable = '0003',
  SlotsExistForDate = '0004',
  TableAlreadyUsed = '0005',
  SlotHasReservations = '0006',
  ResourceNotFound = '1000',
  RestaurantNotFound = '1001',
  PassengerNotFound = '1002',
  ReservationNotFound = '1003',
  EmployeeNotFound = '1004',
  SlotNotFound = '1005',
}

export class Constants {
  public static TITLE_INFO = 'INFO!';
  public static TITLE_ERROR = 'ERROR!';
  public static TITLE_WARNING = 'WARNING!';
  public static TITLE_SUCCESS = 'SUCCESS!';
  public static TITLE_OFFLINE = 'IS OFFLINE!';

  public static RETENTION_WINDOW = 10;

  static Notifications = {
    LIFE: 5000,
    SUCCESS_KEY: 'restaurant-notification',
    ERROR_KEY: 'error',
  };

  static ErrorMessages = {
    ErrorCodes: {
      [ErrorCode.UserRoleNotFound]: 'errors.user_role_not_found',
      [ErrorCode.ForbiddenOperation]: 'errors.forbidden_operation',
      [ErrorCode.DuplicateTable]: 'errors.duplicate_table_name',
      [ErrorCode.SlotsExistForDate]: 'errors.slots_exist_for_date',
      [ErrorCode.TableAlreadyUsed]: 'errors.table_already_used',
      [ErrorCode.SlotHasReservations]: 'errors.slot_has_reservations',
      [ErrorCode.ResourceNotFound]: 'errors.resource_not_found',
      [ErrorCode.RestaurantNotFound]: 'errors.restaurant_not_found',
      [ErrorCode.PassengerNotFound]: 'errors.passenger_not_found',
      [ErrorCode.ReservationNotFound]: 'errors.reservation_not_found',
      [ErrorCode.EmployeeNotFound]: 'errors.employee_not_found',
      [ErrorCode.SlotNotFound]: 'errors.slot_not_found',
    } as const,

    getTranslationKey(code: string, defaultKey = 'errors.generic'): string {
      return code in this.ErrorCodes
        ? this.ErrorCodes[code as keyof typeof this.ErrorCodes]
        : defaultKey;
    },
  };

  static Auth = class {
    public static USER_KEY = 'user';
    public static ENV_KEY = 'environment';
  };

  static Storager = class {
    public static CURRENT_USER: string = 'currentUser';
    public static LANG: string = 'lang';
  };

  static XLSX = class {
    public static xlsxColumns_event: { [key: string]: string[][] } = {
      it: [
        [
          'Codice Prenotazione',
          'Data Prenotazione',
          'Data Slot',
          'Numero di Cabina',
          'Nome Passeggero',
          'Età',
          'Livello Costa Club',
          'Numero Badge',
          'Numero di Partecipanti',
          'Numero Tavolo',
          'My Italian',
          'Note',
        ],
      ],
      en: [
        [
          'Booking Code',
          'Booking Date',
          'Slot Date',
          'Cabin Number',
          'Passenger Name',
          'Age',
          'Costa Club Level',
          'Badge Number',
          'Number of Attendees',
          'Table Number',
          'My Italian',
          'Notes',
        ],
      ],
    };
  };

  static Routing = class {
    public static LOGIN = {
      label: 'Login',
      path: 'login',
      routerLink: ['/login'],
      roles: [],
    };
    public static LANDING = {
      label: 'Landing',
      path: 'landing',
      routerLink: ['/landing'],
      roles: [],
    };
    public static HOME = {
      label: 'Home',
      path: 'home',
      routerLink: ['/home'],
      roles: [],
    };
    public static HOME_PRENOTAZIONI = {
      label: 'Home Prenotazioni',
      path: 'home-prenotazioni',
      routerLink: ['/home-prenotazioni'],
      roles: [Roles.OPERATORE, Roles.ADMIN],
      breadcrumbLabel: 'breadcrumb.bookings',
    };
    public static HOME_RISTORANTI = {
      label: 'Home Ristoranti',
      path: 'home-ristoranti',
      routerLink: ['/home-ristoranti'],
      roles: [Roles.ADMIN],
      breadcrumbLabel: 'breadcrumb.restaurants',
    };
    public static NUOVO_RISTORANTE = {
      label: 'Crea Ristorante',
      path: 'crea-ristorante',
      routerLink: ['/crea-ristorante'],
      roles: [Roles.ADMIN],
      breadcrumbLabel: 'breadcrumb.new_restaurant',
    };
    public static MODIFICA_RISTORANTE = {
      label: 'Modifica Ristorante',
      path: 'modifica-ristorante',
      routerLink: ['/modifica-ristorante'],
      roles: [Roles.ADMIN],
      breadcrumbLabel: 'breadcrumb.edit_restaurant',
    };
    public static GESTIONE_SLOT = {
      label: 'Gestione Slot',
      path: 'slot-management',
      routerLink: ['/slot-management'],
      roles: [Roles.ADMIN],
      breadcrumbLabel: 'breadcrumb.manage_slots',
    };
    public static MODIFICA_SLOT = {
      label: 'Modifica Slot',
      path: 'edit-slot',
      routerLink: ['/edit-slot'],
      roles: [Roles.ADMIN],
      breadcrumbLabel: 'breadcrumb.edit_slot',
    };
    public static NOT_FOUND = {
      label: 'Not Found',
      path: 'not-found',
      routerLink: ['/not-found'],
    };
    public static ADMINISTRATION = {
      label: 'Amministrazione',
      path: 'administration',
      routerLink: ['/administration'],
      roles: [Roles.OPERATORE, Roles.ADMIN],
      breadcrumbLabel: 'breadcrumb.reservation_administration',
    };
    public static ADMINISTRATION_TABLE = {
      label: 'Amministrazione',
      path: 'administration-table',
      routerLink: ['/administration-table'],
      roles: [Roles.OPERATORE, Roles.ADMIN],
      breadcrumbLabel: 'breadcrumb.passenger_reservations',
    };
    public static EXTRACTION = {
      label: 'Estrazione',
      path: 'extraction',
      routerLink: ['/extraction'],
      roles: [Roles.OPERATORE, Roles.ADMIN],
      breadcrumbLabel: 'breadcrumb.reservation_extraction',
    };
    public static NOT_ALLOWED = {
      label: 'Not allowed',
      path: 'not-allowed',
      routerLink: ['/allowed'],
      roles: [],
    };
  };

  // Add new Routes here

  static canAccess(roles: Roles[], route: any) {
    if (route.roles.length == 0) {
      return true;
    }
    return (
      route.roles.find((role: Roles) => {
        return roles.includes(role);
      }) != undefined
    );
  }

  static getRoute(path: string) {
    path = '/' + path;
    return Object.entries(Constants.Routing).find(([key, value]) => {
      return value.routerLink[0] == path;
    });
  }
}

const CRUMBS: { [key: string]: MenuItem[] } = {
  home: [],
  'home-prenotazioni': [
    {
      label: Constants.Routing.HOME_PRENOTAZIONI.breadcrumbLabel,
      routerLink: Constants.Routing.HOME_PRENOTAZIONI.routerLink,
    },
  ],
  'home-ristoranti': [
    {
      label: Constants.Routing.HOME_RISTORANTI.breadcrumbLabel,
      routerLink: Constants.Routing.HOME_RISTORANTI.routerLink,
    },
  ],
  'crea-ristorante': [
    {
      label: Constants.Routing.HOME_RISTORANTI.breadcrumbLabel,
      routerLink: Constants.Routing.HOME_RISTORANTI.routerLink,
    },
    {
      label: Constants.Routing.NUOVO_RISTORANTE.breadcrumbLabel,
      routerLink: Constants.Routing.NUOVO_RISTORANTE.routerLink,
    },
  ],
  'modifica-ristorante': [
    {
      label: Constants.Routing.HOME_RISTORANTI.breadcrumbLabel,
      routerLink: Constants.Routing.HOME_RISTORANTI.routerLink,
    },
    {
      label: Constants.Routing.MODIFICA_RISTORANTE.breadcrumbLabel,
      routerLink: Constants.Routing.MODIFICA_RISTORANTE.routerLink,
    },
  ],
  'slot-management': [
    {
      label: Constants.Routing.HOME_RISTORANTI.breadcrumbLabel,
      routerLink: Constants.Routing.HOME_RISTORANTI.routerLink,
    },
    {
      label: Constants.Routing.GESTIONE_SLOT.breadcrumbLabel,
      routerLink: Constants.Routing.GESTIONE_SLOT.routerLink,
    },
  ],
  'edit-slot': [
    {
      label: Constants.Routing.HOME_RISTORANTI.breadcrumbLabel,
      routerLink: Constants.Routing.HOME_RISTORANTI.routerLink,
    },
    {
      label: Constants.Routing.GESTIONE_SLOT.breadcrumbLabel,
      routerLink: Constants.Routing.GESTIONE_SLOT.routerLink,
    },
    {
      label: Constants.Routing.MODIFICA_SLOT.breadcrumbLabel,
      routerLink: Constants.Routing.MODIFICA_SLOT.routerLink,
    },
  ],
  extraction: [
    {
      label: Constants.Routing.HOME_PRENOTAZIONI.breadcrumbLabel,
      routerLink: Constants.Routing.HOME_PRENOTAZIONI.routerLink,
    },
    {
      label: Constants.Routing.EXTRACTION.breadcrumbLabel,
      routerLink: Constants.Routing.EXTRACTION.routerLink,
    },
  ],
  administration: [
    {
      label: Constants.Routing.HOME_PRENOTAZIONI.breadcrumbLabel,
      routerLink: Constants.Routing.HOME_PRENOTAZIONI.routerLink,
    },
    {
      label: Constants.Routing.ADMINISTRATION.breadcrumbLabel,
      routerLink: Constants.Routing.ADMINISTRATION.routerLink,
    },
  ],
  'administration-table': [
    {
      label: Constants.Routing.HOME_PRENOTAZIONI.breadcrumbLabel,
      routerLink: Constants.Routing.HOME_PRENOTAZIONI.routerLink,
    },
    {
      label: Constants.Routing.ADMINISTRATION.breadcrumbLabel,
      routerLink: Constants.Routing.ADMINISTRATION.routerLink,
    },
    {
      label: Constants.Routing.ADMINISTRATION_TABLE.breadcrumbLabel,
      routerLink: Constants.Routing.ADMINISTRATION_TABLE.routerLink,
    },
  ],
};

export { CRUMBS };
